import type { Place, ViewState } from "../../types/warehouse.types";
import { CachedDimensions, RENDER_CONSTANTS, PlaceRendererConfig } from "../core/types";

/**
 * Caches geometric calculations for places
 *
 * Pattern: Flyweight Pattern
 */
export class PlaceLayoutCache {
  private cache = new Map<string, CachedDimensions>();

  /**
   * Get dimensions with caching
   */
  getDimensions(
    place: Place,
    viewState: ViewState,
    config: PlaceRendererConfig
  ): CachedDimensions {
    const { placeDimensions } = place;
    // Quantize scale to improve cache hit rate
    const qScale = viewState.scale.toFixed(RENDER_CONSTANTS.SCALE_QUANTIZE_DECIMALS);
    const key = `${placeDimensions.width}_${placeDimensions.height}_${placeDimensions.levelHeight}_${qScale}`;

    let dimensions = this.cache.get(key);

    if (!dimensions) {
      dimensions = {
        placeWidth: placeDimensions.width * viewState.scale,
        placeHeight: placeDimensions.height * viewState.scale,
        placeSpacing: placeDimensions.height * config.spacingFactor * viewState.scale,
        isoDepthX: placeDimensions.width * config.isoDepthFactorX * viewState.scale,
        isoDepthY: placeDimensions.width * config.isoDepthFactorY * viewState.scale,
      };
      this.cache.set(key, dimensions);
    }

    return dimensions;
  }

  /**
   * Clear cache
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Run maintenance (LRU cleanup)
   */
  runMaintenance(): void {
    if (this.cache.size > RENDER_CONSTANTS.CACHE_MAX_SIZE) {
      const keysToRemove = Array.from(this.cache.keys()).slice(
        0,
        this.cache.size - Math.floor(RENDER_CONSTANTS.CACHE_MAX_SIZE * 0.8)
      );
      keysToRemove.forEach((key) => this.cache.delete(key));
    }
  }

  /**
   * Get cache size
   */
  get size(): number {
    return this.cache.size;
  }
}
